/**
 * BOM header editor — the fields, not the lines.
 *
 * Functional spec: the BOM properties dialog in `qt_poc/bom_qt.py`.
 *
 * `vat_pct` and `shipping_pct` are stored on the **0–100 scale** (17.0 means
 * 17%), not as fractions. The inputs show and store the same number, so no
 * conversion happens here — the one place a `/100` would be silently wrong.
 */
import { useEffect, useState } from 'react'
import {
  Alert, Button, Group, NumberInput, Select, Stack, Textarea, TextInput,
} from '@mantine/core'

import { Modal } from '../components/AppModal'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'

import { ApiError, api } from '../api/client'
import type { BOM, InventorySnapshot, MasterData } from '../api/types'
import { useQuery } from '@tanstack/react-query'
import { CODE_STYLE } from '../theme'

function blank(): BOM {
  return {
    id: null, project_name: '', budget: 0, budget_mode: 'USD', status: 'טיוטה',
    snapshot_id: null, vat_pct: 17, shipping_pct: 0, notes: '',
    order_number: '', maor_number: '', emf_number: '', dealer_id: null,
    lines: [], created_at: '', updated_at: '',
  }
}

interface Props {
  opened: boolean
  bom: BOM | null
  master?: MasterData
  onClose: () => void
  onSaved?: (bom: BOM) => void
}

export function BomEditorModal({ opened, bom, master, onClose, onSaved }: Props) {
  const qc = useQueryClient()
  const [draft, setDraft] = useState<BOM>(blank)
  const [error, setError] = useState<string | null>(null)

  const snapshots = useQuery({
    queryKey: ['snapshots'],
    queryFn: () => api.get<InventorySnapshot[]>('/inventory/snapshots'),
    enabled: opened,
  })

  useEffect(() => {
    if (!opened) return
    setDraft(bom ? structuredClone(bom) : blank())
    setError(null)
  }, [opened, bom])

  const save = useMutation({
    mutationFn: (value: BOM) =>
      value.id == null
        ? api.post<BOM>('/boms', value)
        : api.put<BOM>(`/boms/${value.id}`, value),
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: ['boms'] })
      void qc.invalidateQueries({ queryKey: ['bom', saved.id] })
      void qc.invalidateQueries({ queryKey: ['bom-expanded', saved.id] })
      notifications.show({ message: 'ה-BOM נשמר', color: 'green' })
      onSaved?.(saved)
      onClose()
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'שמירת ה-BOM נכשלה.'),
  })

  function update(patch: Partial<BOM>) {
    setDraft((d) => ({ ...d, ...patch }))
  }

  function submit() {
    setError(null)
    if (!draft.project_name.trim()) return setError('יש להזין שם פרויקט.')
    save.mutate(draft)
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={bom ? `עריכת BOM — ${bom.project_name}` : 'BOM חדש'}
      size="lg"
    >
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}

        <TextInput
          label="שם פרויקט"
          required
          value={draft.project_name}
          onChange={(e) => update({ project_name: e.currentTarget.value })}
        />

        <Group grow>
          <Select
            label="דילר"
            searchable
            clearable
            clearButtonProps={{ "aria-label": "נקה", size: "md" }}
            data={(master?.dealers ?? []).map((d) => ({
              value: String(d.id), label: d.name,
            }))}
            value={draft.dealer_id ? String(draft.dealer_id) : null}
            onChange={(v) => update({ dealer_id: v ? Number(v) : null })}
          />
          <Select
            label="צילום מלאי"
            description="לניכוי מלאי קיים מהכמויות"
            clearable
            clearButtonProps={{ "aria-label": "נקה", size: "md" }}
            data={(snapshots.data ?? []).map((s) => ({
              value: String(s.id), label: s.snapshot_name,
            }))}
            value={draft.snapshot_id ? String(draft.snapshot_id) : null}
            onChange={(v) => update({ snapshot_id: v ? Number(v) : null })}
          />
        </Group>

        <Group grow>
          <NumberInput
            label="תקציב"
            hideControls
            min={0}
            decimalScale={2}
            thousandSeparator=","
            value={draft.budget}
            onChange={(v) => update({ budget: Number(v) || 0 })}
          />
          <Select
            label="מטבע"
            data={['USD', 'ILS']}
            value={draft.budget_mode}
            onChange={(v) => update({ budget_mode: v ?? 'USD' })}
          />
        </Group>

        <Group grow>
          {/* Percent scale, not fractions: 17 means 17%. */}
          <NumberInput
            label='מע"מ %'
            hideControls
            min={0}
            max={100}
            decimalScale={2}
            value={draft.vat_pct}
            onChange={(v) => update({ vat_pct: Number(v) || 0 })}
          />
          <NumberInput
            label="משלוח %"
            hideControls
            min={0}
            max={100}
            decimalScale={2}
            value={draft.shipping_pct}
            onChange={(v) => update({ shipping_pct: Number(v) || 0 })}
          />
        </Group>

        <Group grow>
          <TextInput
            label="מספר הזמנה"
            value={draft.order_number}
            onChange={(e) => update({ order_number: e.currentTarget.value })}
            styles={{ input: CODE_STYLE }}
          />
          <TextInput
            label="מאור"
            value={draft.maor_number}
            onChange={(e) => update({ maor_number: e.currentTarget.value })}
            styles={{ input: CODE_STYLE }}
          />
          <TextInput
            label="EMF"
            value={draft.emf_number}
            onChange={(e) => update({ emf_number: e.currentTarget.value })}
            styles={{ input: CODE_STYLE }}
          />
        </Group>

        <Textarea
          label="הערות"
          autosize
          minRows={2}
          value={draft.notes}
          onChange={(e) => update({ notes: e.currentTarget.value })}
        />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>ביטול</Button>
          <Button onClick={submit} loading={save.isPending}>שמור</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
